import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined

const EditFinanceSetup = () => {
    const { id } = useParams(); // Get the finance setup ID from the URL
    const navigate = useNavigate();

    // State for holding the finance setup data
    const [financeSetup, setFinanceSetup] = useState({
        expenseBudgetControl: '',
        tdsApplicable: false,
        documentClassification: false,
        allowNegativeCash: false,
        transactionOnBehalf: false,
        bankReconciliation: '',
        manageBillwiseOutstanding: '',
        zoomLevel: '',
        attributes: [], // Default to an empty array
    });

    // Fetch finance setup details on component mount
    useEffect(() => {
        fetch(`${BASE_URL}financesetup/${id}`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 'success') {
                    setFinanceSetup(prevState => ({
                        ...prevState,
                        ...data.data,
                        attributes: Array.isArray(data.data.attributes) ? data.data.attributes : [], // Ensure attributes is an array
                    }));
                }
            })
            .catch((error) => console.error('Error fetching finance setup details:', error));
    }, [id]);

    // Handle form submission
    const handleSubmit = (event) => {
        event.preventDefault();

        fetch(`${BASE_URL}financesetup/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(financeSetup),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                if (data.status === 'success') {
                    alert('Finance setup updated successfully!');
                    navigate('/EditFinanceSetup/1'); // Assuming the route to list is `/FinanceSetupList`
                } else {
                    alert('Error updating finance setup. Please try again.');
                }
            })
            .catch((error) => {
                console.error('Error updating finance setup:', error);
                alert('Error updating finance setup. Please check the console for more details.');
            });
    };

    // Handle input change for all fields
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFinanceSetup({ ...financeSetup, [name]: checked });
        } else if (name.startsWith('attributes')) {
            const attributeIndex = parseInt(name.split('_')[1]);
            const attributeName = name.split('_')[0];
            const updatedAttributes = [...financeSetup.attributes];
            updatedAttributes[attributeIndex] = { ...updatedAttributes[attributeIndex], [attributeName]: value };
            setFinanceSetup({ ...financeSetup, attributes: updatedAttributes });
        } else {
            setFinanceSetup({ ...financeSetup, [name]: value });
        }
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Edit Finance Setup</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb"></ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Finance Setup Form */}
            <section className="section">
                <div className="row">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header"></div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    {/* Expense Budget Control - Dropdown */}
                                    <div className="mb-3">
                                        <label htmlFor="expenseBudgetControl" className="form-label">Expense Budget Control</label>
                                        <select
                                            id="expenseBudgetControl"
                                            name="expenseBudgetControl"
                                            className="form-control"
                                            value={financeSetup.expenseBudgetControl}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Expense Budget Control</option>
                                            <option value="NotRequired">Not Required</option>
                                            <option value="GLBranchMonth">GL Branch Month</option>
                                            <option value="GLGroupProjectAttribute">GL Group Project Attribute</option>
                                        </select>
                                    </div>

                                    {/* TDS Applicable */}
                                    <div className="mb-3">
                                        <label htmlFor="tdsApplicable" className="form-label">TDS Applicable</label>
                                        <input
                                            type="checkbox"
                                            id="tdsApplicable"
                                            name="tdsApplicable"
                                            className="form-check-input"
                                            checked={financeSetup.tdsApplicable}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Document Classification */}
                                    <div className="mb-3">
                                        <label htmlFor="documentClassification" className="form-label">Document Classification</label>
                                        <input
                                            type="checkbox"
                                            id="documentClassification"
                                            name="documentClassification"
                                            className="form-check-input"
                                            checked={financeSetup.documentClassification}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Allow Negative Cash */}
                                    <div className="mb-3">
                                        <label htmlFor="allowNegativeCash" className="form-label">Allow Negative Cash</label>
                                        <input
                                            type="checkbox"
                                            id="allowNegativeCash"
                                            name="allowNegativeCash"
                                            className="form-check-input"
                                            checked={financeSetup.allowNegativeCash}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Transaction On Behalf */}
                                    <div className="mb-3">
                                        <label htmlFor="transactionOnBehalf" className="form-label">Transaction On Behalf</label>
                                        <input
                                            type="checkbox"
                                            id="transactionOnBehalf"
                                            name="transactionOnBehalf"
                                            className="form-check-input"
                                            checked={financeSetup.transactionOnBehalf}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Bank Reconciliation */}
                                    <div className="mb-3">
                                        <label htmlFor="bankReconciliation" className="form-label">Bank Reconciliation</label>
                                        <input
                                            type="text"
                                            id="bankReconciliation"
                                            name="bankReconciliation"
                                            className="form-control"
                                            value={financeSetup.bankReconciliation}
                                            onChange={handleChange}
                                            placeholder="Enter bank reconciliation method"
                                        />
                                    </div>

                                    {/* Manage Billwise Outstanding */}
                                    <div className="mb-3">
                                        <label htmlFor="manageBillwiseOutstanding" className="form-label">Manage Billwise Outstanding</label>
                                        <input
                                            type="text"
                                            id="manageBillwiseOutstanding"
                                            name="manageBillwiseOutstanding"
                                            className="form-control"
                                            value={financeSetup.manageBillwiseOutstanding}
                                            onChange={handleChange}
                                            placeholder="Enter billwise outstanding management method"
                                        />
                                    </div>

                                    {/* Zoom Level */}
                                    <div className="mb-3">
                                        <label htmlFor="zoomLevel" className="form-label">Zoom Level</label>
                                        <input
                                            type="text"
                                            id="zoomLevel"
                                            name="zoomLevel"
                                            className="form-control"
                                            value={financeSetup.zoomLevel}
                                            onChange={handleChange}
                                            placeholder="Enter zoom level"
                                        />
                                    </div>

                                    {/* Attributes - only render if attributes is an array */}
                                    {Array.isArray(financeSetup.attributes) && financeSetup.attributes.length > 0 && financeSetup.attributes.map((attribute, index) => (
                                        <div key={index} className="mb-3">
                                            <label htmlFor={`attributes_${index}_caption`} className="form-label">Attribute Caption</label>
                                            <input
                                                type="text"
                                                id={`attributes_${index}_caption`}
                                                name={`attributes_${index}_caption`}
                                                className="form-control"
                                                value={attribute.caption}
                                                onChange={handleChange}
                                                placeholder="Enter attribute caption"
                                            />
                                            
                                            <label htmlFor={`attributes_${index}_required`} className="form-label">Required</label>
                                            <input
                                                type="checkbox"
                                                id={`attributes_${index}_required`}
                                                name={`attributes_${index}_required`}
                                                className="form-check-input"
                                                checked={attribute.required}
                                                onChange={handleChange}
                                            />
                                            
                                            <label htmlFor={`attributes_${index}_allowBlank`} className="form-label">Allow Blank</label>
                                            <input
                                                type="checkbox"
                                                id={`attributes_${index}_allowBlank`}
                                                name={`attributes_${index}_allowBlank`}
                                                className="form-check-input"
                                                checked={attribute.allowBlank}
                                                onChange={handleChange}
                                            />
                                            
                                            <label htmlFor={`attributes_${index}_type`} className="form-label">Attribute Type</label>
                                            <input
                                                type="text"
                                                id={`attributes_${index}_type`}
                                                name={`attributes_${index}_type`}
                                                className="form-control"
                                                value={attribute.type}
                                                onChange={handleChange}
                                                placeholder="Enter attribute type"
                                            />
                                        </div>
                                    ))}

                                    <div className="text-end">
                                        <button type="submit" className="btn btn-primary">
                                            Update Finance Setup
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EditFinanceSetup;
